/**
 * Phase DRAWING : monte DrawingBoard sur l'assignment recu, timer pilote par
 * endsAt, envoi automatique a l'expiration. Aucun ecran de confirmation.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import type { RoomSnapshot } from '@retusch/shared';
import { useT } from '../locales/useT.js';
import { connection } from '../net/connection.js';
import { useRoomStore } from '../store/roomStore.js';
import { decodeDrawing } from '@retusch/shared';
import { DrawingBoard, type DrawingResult } from '../game/DrawingBoard.js';
import { DevOptionsStore } from '../game/devOptions.js';
import { loadArtwork } from '../game/artwork.js';
import { Avatar } from '../ui/Avatar.js';
import { Screen } from '../ui/Screen.js';
import { roundLabel } from '../ui/roundLabel.js';
import { GameLayout } from '../ui/GameLayout.js';
import { Watching } from '../ui/Watching.js';
import { SpectatorView } from '../game/SpectatorView.js';

export function DrawingScreen({ room }: { room: RoomSnapshot }): ReactElement {
  const t = useT();
  const assignment = useRoomStore((state) => state.assignment);
  const submittedRound = useRoomStore((state) => state.submittedRound);
  const markSubmitted = useRoomStore((state) => state.markSubmitted);
  const rememberSubmission = useRoomStore((state) => state.rememberSubmission);
  const playerId = useRoomStore((state) => state.playerId);
  const watched = useRoomStore((state) => state.watched);

  const [image, setImage] = useState<HTMLImageElement | null>(null);
  // En partie reelle les bascules de dev restent a leur valeur par defaut.
  const devStore = useMemo(() => new DevOptionsStore(), []);

  useEffect(() => {
    if (assignment === null) return;
    let alive = true;
    setImage(null);
    loadArtwork(assignment.file)
      .then((loaded) => {
        if (alive) setImage(loaded);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [assignment]);

  const onSubmit = useCallback(
    (result: DrawingResult) => {
      if (assignment === null) return;
      markSubmitted(assignment.round);
      // Garde de quoi le remontrer a son auteur pendant la devinette.
      rememberSubmission({
        round: assignment.round,
        file: assignment.file,
        zone: assignment.zone,
        palette: result.palette,
        drawing: result.buffer,
      });
      // La palette part AVANT le blob : elle peut contenir des couleurs
      // prelevees au pipette, absentes de la palette du tableau. Sans elle,
      // ces traits seraient rendus avec un repli chez les autres joueurs.
      connection.send({ type: 'palette', colors: [...result.palette] });
      connection.sendDrawing(result.buffer, assignment.round);
    },
    [assignment, markSubmitted, rememberSubmission],
  );

  /**
   * Instantane pour les spectateurs. La palette part avec, et a chaque fois :
   * une couleur prise a la pipette n'existe que chez son auteur, sans elle le
   * spectateur rendrait ces traits avec un repli.
   */
  const onPreview = useCallback(
    (result: DrawingResult) => {
      connection.send({ type: 'palette', colors: [...result.palette] });
      connection.sendPreview(result.buffer, assignment?.round ?? 0);
    },
    [assignment?.round],
  );

  /**
   * Mode collaboratif : le travail des precedents, decode une seule fois.
   *
   * On attend d'avoir TOUTES les couches (`expected`) avant de decoder : un
   * fond monte trame par trame apparaitrait sous les yeux du joueur, qui aurait
   * deja commence a dessiner par-dessus un tableau incomplet.
   */
  const base = useMemo(() => {
    if (assignment === null || assignment.expected === 0) return undefined;
    if (assignment.layers.filter(Boolean).length < assignment.expected) return undefined;
    const palettes = assignment.layerPalettes;
    return assignment.layers.flatMap((buffer, index) => {
      try {
        return [
          {
            strokes: decodeDrawing(buffer),
            palette: palettes[index] ?? assignment.palette,
          },
        ];
      } catch {
        // Une couche illisible ne doit pas emporter la manche : on perd le
        // travail d'un joueur a l'ecran, pas la partie.
        return [];
      }
    });
  }, [assignment]);

  /**
   * La deadline porte sur LA MANCHE DE CE PLATEAU, jamais sur la seule phase.
   *
   * Le serveur envoie l'assignment de la manche suivante AVANT son instantane
   * DRAWING : pendant cette fenetre, la phase vaut encore COLLECTING, et un
   * plateau neuf qui s'y fiait soumettait un dessin VIDE des son montage. Les
   * manches brulaient alors en cascade, 100 ms chacune, des que l'image
   * sortait du cache du navigateur. Vu en vrai avec trois clients Chrome : la
   * manche 2 entiere jouee en 0,1 s, et la partie finie apres deux tableaux.
   */
  const deadlinePassed =
    assignment !== null && room.phase !== 'DRAWING' && room.round >= assignment.round;

  const alreadySent = assignment !== null && submittedRound === assignment.round;

  // Pas de tableau a redessiner : soit on est spectateur (elimine), soit un
  // seul joueur dessine cette manche. Sans cet ecran, les deux nouveaux modes
  // laissaient leurs joueurs sur un chargement qui ne finissait jamais.
  const self = room.players.find((player) => player.id === playerId);

  // Sans role cette manche : on ne subit pas la partie, on la regarde. Le
  // spectateur choisit qui suivre et voit son dessin se construire.
  if (assignment === null && (self?.waiting === true || self?.eliminated === true)) {
    return (
      <Screen name="drawing-spectator">
        <GameLayout room={room} selfId={playerId}>
          <SpectatorView
            room={room}
            selfId={playerId}
            durationMs={room.settings.drawTime * 1000}
          />
        </GameLayout>
      </Screen>
    );
  }

  if (assignment === null) {
    const drawer = room.players.find((player) => player.done === false && !player.eliminated);
    // Trois raisons de ne pas dessiner, trois messages. Celui qui vient
    // d'arriver en pleine partie doit savoir qu'il entre au tour suivant : sans
    // cela, son premier contact avec le jeu est un ecran d'attente muet.
    const title =
      self?.waiting === true
        ? t('game.joinedTitle')
        : self?.eliminated === true
          ? t('game.spectator')
          : // Le streamer ne dessine pas, et TOUS les autres dessinent :
            // nommer un joueur au hasard, comme en « un seul dessine »,
            // designerait la mauvaise personne.
            self?.streamer === true
            ? t('game.streamerWait')
            : t('game.watchDrawing', { name: drawer?.nickname ?? '?' });
    const subtitle =
      self?.waiting === true
        ? t('game.joinedHint')
        : self?.eliminated === true
          ? t('game.spectatorHint')
          : self?.streamer === true
            ? t('game.streamerWaitHint')
            : t('game.watchDrawingHint');

    return (
      <Screen name="drawing-watching">
        <GameLayout room={room} selfId={playerId}>
          <Watching
            title={title}
            subtitle={subtitle}
            room={room}
            durationMs={room.settings.drawTime * 1000}
          />
        </GameLayout>
      </Screen>
    );
  }

  if (image === null) {
    return (
      <Screen name="drawing-loading">
        <p className="sandbox__status">{t('drawing.locating')}</p>
      </Screen>
    );
  }

  // Dessin rendu : on attend les autres. Le plateau reste monte, gele, sous le
  // panneau d'attente : on veut continuer a voir sa propre retouche pendant que
  // les coches se remplissent, pas la perdre au profit d'un ecran vide.
  const waiting = alreadySent ? (
    <div className="waiting-strip">
      <p className="waiting-strip__title">{t('drawing.submitted')}</p>
      <p className="waiting-strip__subtitle">{t('drawing.waiting')}</p>
      <div className="waiting-strip__players">
        {room.players.map((player) => (
          <div className="waiting-strip__player" key={player.id}>
            <Avatar
              playerId={player.id} avatar={player.avatar}
              colorIndex={player.colorIndex}
              size={34}
              dimmed={!player.connected}
              done={player.done}
            />
            <span className="waiting-strip__name">{player.nickname}</span>
          </div>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <Screen name={`drawing-${assignment.round}`}>
      <GameLayout room={room} selfId={playerId}>
        <DrawingBoard
        key={`${assignment.file}-${assignment.round}`}
        image={image}
        zone={assignment.zone}
        durationMs={room.settings.drawTime * 1000}
        endsAt={room.endsAt}
        dev={devStore}
        submitLabel={t('drawing.submit')}
        roundLabel={roundLabel(t, room, assignment.round)}
        waiting={waiting}
        deadlinePassed={deadlinePassed}
        watched={watched}
        {...(base === undefined ? {} : { base })}
        onPreview={onPreview}
          onSubmit={onSubmit}
        />
      </GameLayout>
    </Screen>
  );
}
